import {
    Injectable,
    Inject,
    PLATFORM_ID
} from '@angular/core';

import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpContextToken
} from '@angular/common/http';

import { isPlatformBrowser } from '@angular/common';

import {
    Observable,
    throwError
} from 'rxjs';

import {
    catchError,
    finalize,
    map,
    shareReplay,
    switchMap,
    tap
} from 'rxjs/operators';

import { ProgressBarService } from '../_services/progress-bar.service';
import { AuthenticationService } from '../_services/authentication.service';
import { ToastrService } from 'ngx-toastr';
export const AUTH_RETRY_REQUEST =
    new HttpContextToken<boolean>(
        () => false
    );
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private refreshInProgress$:
        Observable<any> | null = null;


    constructor(
        private progressBarService: ProgressBarService,
        private toastr: ToastrService,
        private authService: AuthenticationService,

        @Inject(PLATFORM_ID)
        private platformId: Object
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {

        return next.handle(request).pipe(

            catchError(
                (err: HttpErrorResponse) => {

                    return this.handleError(
                        request,
                        next,
                        err
                    );
                }
            )
        );
    }

    private handleError(
        request: HttpRequest<any>,
        next: HttpHandler,
        err: HttpErrorResponse
    ): Observable<HttpEvent<any>> {

        let errorMessage =
            err.statusText ||
            'An error occurred';


        if (
            typeof err.error === 'string'
        ) {

            errorMessage = err.error;

        } else if (
            err.error?.message
        ) {

            errorMessage =
                err.error.message;
        }


        if (
            err.status === 401 ||
            err.status === 403
        ) {

            return this.handleAuthenticationError(
                request,
                next,
                err
            );
        }


  
        if (
            err.status === 500 ||
            err.status === 504
        ) {

            this.toastr.error(
                errorMessage
            );
        }


        else if (
            err.status === 400 ||
            err.status === 404
        ) {

     
            if (
                err.error?.error ===
                'invalid_grant'
            ) {

                this.toastr.error(
                    'Wrong username and password'
                );

            } else {

                // Show field-level validation errors.
                const fieldErrors =
                    err.error?.errors;


                if (
                    fieldErrors &&
                    typeof fieldErrors === 'object'
                ) {

                    Object.values(
                        fieldErrors
                    ).forEach(
                        (message: any) => {

                            this.toastr.error(
                                String(message)
                            );
                        }
                    );

                } else {

                    this.toastr.error(
                        errorMessage
                    );
                }
            }
        }


      
        return throwError(
            () => err
        );
    }


  
    private handleAuthenticationError(
        request: HttpRequest<any>,
        next: HttpHandler,
        err: HttpErrorResponse
    ): Observable<HttpEvent<any>> {


        if (
            request.context.get(
                AUTH_RETRY_REQUEST
            )
        ) {

            console.warn(
                'Authentication retry failed:',
                request.url
            );

            this.handleSessionExpired();

            return throwError(
                () => err
            );
        }


        const backendError =
            typeof err.error === 'object'
                ? err.error?.error
                : null;


        const backendMessage =
            typeof err.error === 'object'
                ? err.error?.message
                : null;

        const tokenExpired =
            backendError ===
                'token_expired' ||
            backendMessage ===
                'Token expired';


        const invalidToken =
            backendError ===
            'invalid_token';



        const shouldRefresh =
            tokenExpired ||
            invalidToken;


        if (!shouldRefresh) {

            const backendMessage =
                typeof err.error === 'object'
                    ? err.error?.message
                    : null;

            if (backendMessage) {
                this.toastr.error(
                    backendMessage
                );
            }

            return throwError(
                () => err
            );
        }


  
        if (
            !isPlatformBrowser(
                this.platformId
            )
        ) {

            return throwError(
                () => err
            );
        }


  
        const tokenDetails =
            this.authService.getTokenDetails();


        const refreshToken =
            tokenDetails?.refresh_token;


        if (
            !refreshToken
        ) {

            console.warn(
                'Token expired but no refresh token is available.'
            );

            this.handleSessionExpired();

            return throwError(
                () => err
            );
        }


        return this.refreshTokenOnce(
            refreshToken
        ).pipe(

            switchMap(
                (refreshResponse: any) => {

                    if (
                        !refreshResponse ||
                        !refreshResponse.access_token
                    ) {

                        console.error(
                            'Refresh succeeded but access_token is missing.',
                            refreshResponse
                        );

                        this.handleSessionExpired();

                        return throwError(
                            () => err
                        );
                    }

                    const retryRequest =
                        request.clone({

                            context:
                                request.context.set(
                                    AUTH_RETRY_REQUEST,
                                    true
                                ),

                            headers:
                                request.headers.set(
                                    'Authorization',
                                    'Bearer ' +
                                    refreshResponse.access_token
                                )
                        });


                    console.log(
                        'Token refresh effective - retrying request with new token:',
                        request.url
                    );

                    return next.handle(
                        retryRequest
                    );
                }
            ),

            catchError(
                (refreshError) => {

                    console.error(
                        'Token refresh failed:',
                        refreshError
                    );


                    this.handleSessionExpired();


                    return throwError(
                        () => refreshError
                    );
                }
            )
        );
    }


    private refreshTokenOnce(
        refreshToken: string
    ): Observable<any> {


    
        if (
            this.refreshInProgress$
        ) {

            console.log(
                'Token refresh already in progress - waiting for existing refresh.'
            );

            return this.refreshInProgress$;
        }


        console.log(
            'Starting token refresh...'
        );


     
        const refresh$ =
            this.authService
                .refreshToken(refreshToken)
                .pipe(

                    tap(
                        (refreshResponse: any) => {

                            console.log(
                                'Refresh token response received.'
                            );


                            if (
                                !refreshResponse ||
                                !refreshResponse.access_token
                            ) {

                                throw new Error(
                                    'Refresh response does not contain access_token'
                                );
                            }


                        
                            const saved =
                                this.authService.saveTokenDetails(
                                    refreshResponse
                                );


                            if (!saved) {

                                throw new Error(
                                    'Failed to persist refreshed authentication tokens'
                                );
                            }


                            console.log(
                                'New access token persisted and verified.'
                            );
                        }
                    ),

                  
                    shareReplay({
                        bufferSize: 1,
                        refCount: false
                    }),

                  
                    finalize(
                        () => {

                            console.log(
                                'Token refresh operation completed.'
                            );

                            this.refreshInProgress$ =
                                null;
                        }
                    )
                );


        this.refreshInProgress$ =
            refresh$;


        return refresh$;
    }


  
    private handleSessionExpired(): void {

    
        if (
            !isPlatformBrowser(
                this.platformId
            )
        ) {

            return;
        }


        console.warn(
            'Session expired - logging user out.'
        );


        this.authService.logout();


        setTimeout(
            () => {

                if (
                    isPlatformBrowser(
                        this.platformId
                    )
                ) {

                    window.location.reload();
                }

            },
            100
        );
    }
}